import React, { useEffect, useRef, useState } from "react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../components/ui/carousel";
import { CalendarDays, QrCode, Receipt, FileSignature, FileSpreadsheet, Brain } from "lucide-react";

/** Feature carousel: illustrations built from CSS/SVG mockups — no external generic images. */

function CardShell({ children, tone = "light", label }) {
  const dark = tone === "dark";
  const accent = tone === "accent";
  return (
    <div className={`card-smart p-8 md:p-10 h-[440px] relative overflow-hidden ${dark ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : accent ? "bg-[#FF7A29] text-white border-[#FF7A29]" : ""}`}>
      <div className={`text-[10px] uppercase tracking-[0.25em] mb-4 ${accent ? "text-white/70" : dark ? "text-[#FF7A29]" : "text-[#FF7A29]"}`}>{label}</div>
      {children}
    </div>
  );
}

function MockCalendar() {
  const days = Array.from({ length: 30 }, (_, i) => i + 1);
  const busy = [3, 7, 12, 15, 18, 22, 25, 28];
  return (
    <div>
      <h3 className="font-display text-3xl tracking-tight mb-6">Agenda Febbraio</h3>
      <div className="grid grid-cols-7 gap-1 text-center">
        {["L", "M", "M", "G", "V", "S", "D"].map((d, i) => (
          <div key={i} className="text-[10px] uppercase tracking-widest text-[#757575] py-1">{d}</div>
        ))}
        {days.map((d) => (
          <div key={d} className={`aspect-square flex items-center justify-center text-xs border ${busy.includes(d) ? "bg-[#FF7A29] text-white border-[#FF7A29]" : "bg-white text-black border-[#EDE5D8]"}`}>
            {d}
          </div>
        ))}
      </div>
      <div className="mt-6 border-t border-[#EDE5D8] pt-4 text-xs">
        <div className="flex items-center justify-between py-1">
          <span className="font-semibold">10:00 · Call team vendite</span>
          <span className="bg-black text-white px-2 py-0.5 text-[10px]">15m</span>
        </div>
        <div className="flex items-center justify-between py-1">
          <span>14:30 · Revisione presenze</span>
          <span className="text-[#757575]">45m</span>
        </div>
      </div>
    </div>
  );
}

function MockQR() {
  return (
    <div>
      <h3 className="font-display text-3xl tracking-tight mb-6">QR + Geofence</h3>
      <div className="flex items-center gap-6">
        <div className="shrink-0 w-32 h-32 bg-white p-3 border border-black">
          <svg viewBox="0 0 21 21" width="100%" height="100%" shapeRendering="crispEdges">
            {[
              "11111110101011111111",
              "10000010111010000001",
              "10111010100010111011",
              "10111010111010111011",
              "10111010010110111010",
              "10000010101010000001",
              "11111110101011111111",
              "00000000100000000000",
              "10101111011101110101",
              "11011010111011010111",
              "00100110010100101101",
              "11011110101110110111",
              "01110111010011010001",
              "00000000111100001101",
              "11111110101000111011",
              "10000010001011001001",
              "10111010101110111110",
              "10111010110111100011",
              "10111010111110001111",
              "10000010010010110001",
              "11111110101011001011",
            ].map((row, y) => row.split("").map((bit, x) => bit === "1" ? <rect key={`${x}-${y}`} x={x} y={y} width="1" height="1" fill="#000"/> : null))}
          </svg>
        </div>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-white/60 mb-2">Sede · Milano Duomo</div>
          <div className="font-display text-xl mb-4">45.4641°N · 9.1900°E</div>
          <div className="text-xs bg-white/10 p-3 border border-white/20">
            <div className="flex justify-between"><span>Raggio</span><span className="font-semibold">100 m</span></div>
            <div className="flex justify-between mt-1"><span>Timbrature oggi</span><span className="font-semibold">24</span></div>
            <div className="flex justify-between mt-1"><span>Fuori raggio</span><span className="text-[#FF7A29] font-semibold">1</span></div>
          </div>
        </div>
      </div>
      <div className="mt-6 pt-4 border-t border-white/10">
        <div className="text-xs text-white/60">Validazione GPS Haversine · raggio configurabile</div>
      </div>
    </div>
  );
}

function MockExpenses() {
  const rows = [
    { d: "02/02", w: "Pranzo cliente", c: "Pasti", a: "47,50", s: "approved" },
    { d: "03/02", w: "Treno Milano-Roma", c: "Trasporti", a: "124,00", s: "pending" },
    { d: "04/02", w: "Hotel Roma", c: "Alloggio", a: "189,00", s: "pending" },
    { d: "05/02", w: "Carburante", c: "Auto", a: "65,00", s: "approved" },
  ];
  return (
    <div>
      <h3 className="font-display text-3xl tracking-tight mb-6">Note Spese</h3>
      <div className="border border-[#EDE5D8]">
        <div className="grid grid-cols-[60px_1fr_80px_70px_80px] text-[10px] uppercase tracking-widest text-[#757575] p-2 border-b border-[#EDE5D8]">
          <span>Data</span><span>Descrizione</span><span>Cat.</span><span>€</span><span>Stato</span>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="grid grid-cols-[60px_1fr_80px_70px_80px] text-xs p-2 items-center border-b border-[#EDE5D8]">
            <span>{r.d}</span>
            <span className="truncate">{r.w}</span>
            <span className="text-[#757575]">{r.c}</span>
            <span className="font-semibold">€{r.a}</span>
            <span className={`text-[10px] px-1.5 py-0.5 inline-block w-fit ${r.s === "approved" ? "bg-[#FF7A29] text-white" : "bg-black text-white"}`}>{r.s === "approved" ? "OK" : "Attesa"}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <span className="text-xs bg-[#FF7A29] text-white px-3 py-1">Totale mese · €1.425</span>
        <span className="text-xs border border-black px-3 py-1">Upload scontrino</span>
      </div>
    </div>
  );
}

function MockSignature() {
  return (
    <div>
      <h3 className="font-display text-3xl tracking-tight mb-6">Firma Digitale</h3>
      <div className="border border-[#EDE5D8] bg-white p-4 relative h-40">
        <svg viewBox="0 0 300 100" className="w-full h-full">
          <path d="M 10 60 Q 30 20 50 50 T 90 55 Q 110 30 130 55 T 180 60 Q 210 40 240 65 T 290 55" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round"/>
        </svg>
        <div className="absolute bottom-2 right-3 text-[10px] text-[#757575]">M. De Angelis · 02.02.2026 · 14:22</div>
      </div>
      <div className="mt-4 bg-[#FFFAF5] border border-[#EDE5D8] p-3 font-mono text-[10px] break-all text-[#555]">
        SHA-256: 8f4e2a9c7b1d6e3f5a8b2c9d4e7f1a6b3c8d5e9f2a7b4c1d6e3f8a5b2c9d4e7f
      </div>
      <div className="mt-3 text-xs text-[#FF7A29] font-semibold">✓ Firma verificata · Valore probatorio</div>
    </div>
  );
}

function MockPayroll() {
  return (
    <div>
      <h3 className="font-display text-3xl tracking-tight mb-6">Export Paghe</h3>
      <pre className="bg-black text-[#FF7A29] p-4 text-[10px] leading-5 overflow-hidden border border-black">{`MATRICOLA;NOMINATIVO;DATA;ENTRATA;USCITA;ORE
E0001;ROSSI MARIO;2026-02-03;09:02;18:15;9.21
E0002;VERDI LUCIA;2026-02-03;08:45;17:30;8.75
E0003;NERI PAOLO;2026-02-03;09:15;18:00;8.75
E0001;ROSSI MARIO;2026-02-04;08:58;18:05;9.11
E0004;BIANCHI S.;2026-02-04;09:00;17:45;8.75
E0005;ESPOSITO F.;2026-02-04;08:50;18:20;9.50`}</pre>
      <div className="mt-4 grid grid-cols-3 gap-2 text-center">
        {["Zucchetti", "TeamSystem", "Inaz"].map((n) => (
          <div key={n} className="border border-[#EDE5D8] p-2 text-xs font-semibold">{n}</div>
        ))}
      </div>
      <div className="mt-4 text-xs text-[#757575]">CSV standard · compatibile con i principali gestionali italiani.</div>
    </div>
  );
}

function MockAI() {
  return (
    <div className="text-black">
      <h3 className="font-display text-3xl tracking-tight mb-6">AI · GPT-5.2</h3>
      <div className="bg-black text-white p-5 text-sm leading-relaxed">
        <div className="text-[10px] uppercase tracking-widest text-[#FF7A29] mb-2">Riepilogo automatico</div>
        <p>Rilevati 3 pattern ricorrenti di entrate tardive il lunedì mattina (+12 min in media). Il team Logistica mostra efficienza sopra la media (+8%).</p>
        <div className="text-[10px] uppercase tracking-widest text-[#FF7A29] mt-4 mb-2">Anomalie</div>
        <ul className="text-xs space-y-1">
          <li>— 2 timbrature fuori raggio sede Milano (04/02)</li>
          <li>— Mancate uscite: 1 dipendente (ricorrente)</li>
          <li>— Straordinari non autorizzati: 5h Team Vendite</li>
        </ul>
      </div>
      <div className="mt-4 text-xs">✓ Report generato in 2.4s · Italiano · Modello OpenAI GPT-5.2</div>
    </div>
  );
}

const SLIDES = [
  { label: "01 · Calendario", tone: "light", cmp: <MockCalendar/>, icon: CalendarDays },
  { label: "02 · Presenze QR + GPS", tone: "dark", cmp: <MockQR/>, icon: QrCode },
  { label: "03 · Rimborsi spese", tone: "light", cmp: <MockExpenses/>, icon: Receipt },
  { label: "04 · Firma digitale", tone: "light", cmp: <MockSignature/>, icon: FileSignature },
  { label: "05 · Export paghe", tone: "light", cmp: <MockPayroll/>, icon: FileSpreadsheet },
  { label: "06 · Analisi AI", tone: "accent", cmp: <MockAI/>, icon: Brain },
];

export default function FeatureCarousel() {
  const [api, setApi] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (!api) return;
    setIdx(api.selectedScrollSnap());
    api.on("select", () => setIdx(api.selectedScrollSnap()));
    const t = setInterval(() => api.scrollNext(), 6000);
    return () => clearInterval(t);
  }, [api]);

  return (
    <section className="bg-[#FFFAF5]" data-testid="feature-carousel">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-8 items-end mb-12">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 bg-[#FF7A29]/10 text-[#E85D00] text-xs font-semibold uppercase tracking-[0.18em] px-4 py-2 rounded-full mb-5">La piattaforma in azione</div>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">Sfoglia le funzioni principali.</h2>
          </div>
          <div className="md:col-span-5 text-[#555]">
            Ogni modulo è progettato per essere operativo dal primo giorno. Nessun setup complesso, nessuna formazione obbligatoria.
          </div>
        </div>

        <Carousel setApi={setApi} opts={{ loop: true, align: "start" }} className="relative">
          <CarouselContent className="-ml-4">
            {SLIDES.map((s, i) => (
              <CarouselItem key={i} className="pl-4 md:basis-1/2 lg:basis-1/2">
                <CardShell tone={s.tone} label={s.label}>
                  {s.cmp}
                </CardShell>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious data-testid="carousel-prev" className="rounded-full border-[#0A0A0A] hover:bg-[#FF7A29] hover:text-white hover:border-[#FF7A29] -left-4 w-11 h-11 shadow-md"/>
          <CarouselNext data-testid="carousel-next" className="rounded-full border-[#0A0A0A] hover:bg-[#FF7A29] hover:text-white hover:border-[#FF7A29] -right-4 w-11 h-11 shadow-md"/>
        </Carousel>

        <div className="flex items-center justify-center gap-2 mt-8" data-testid="carousel-dots">
          {SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => api?.scrollTo(i)}
              aria-label={`Vai allo slide ${i + 1}`}
              data-testid={`carousel-dot-${i}`}
              className={`h-1.5 rounded-full transition-all ${idx === i ? "w-10 bg-[#FF7A29]" : "w-4 bg-[#EDE5D8]"}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
